import { useCallback, useState } from 'react'
import { UploadCloud, Image, FileImage } from 'lucide-react'

interface UploadDropzoneProps {
  onFilesSelected: (files: FileList | null) => void
  isUploading?: boolean
}

export const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  onFilesSelected,
  isUploading,
}) => {
  const [isDragging, setIsDragging] = useState(false)

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      
      const files = e.dataTransfer.files
      onFilesSelected(files)
    },
    [onFilesSelected]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFilesSelected(e.target.files)
    },
    [onFilesSelected]
  )

  return (
    <div
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer overflow-hidden ${
        isDragging
          ? 'border-blue-500 bg-blue-50 scale-[1.02]'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <input
        type="file"
        multiple
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
        id="file-upload"
        disabled={isUploading}
      />
      
      <label htmlFor="file-upload" className="cursor-pointer relative z-10 block">
        <div className={`mx-auto w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-300 ${
          isDragging 
            ? 'bg-blue-500 shadow-lg shadow-blue-500/30' 
            : 'bg-gradient-to-br from-blue-100 to-blue-50'
        }`}>
          <UploadCloud className={`w-10 h-10 transition-colors duration-300 ${
            isDragging ? 'text-white' : 'text-blue-500'
          }`} />
        </div>
        
        <p className="mt-6 text-xl font-semibold text-gray-900">
          {isDragging ? 'Drop your photos here' : 'Drag & drop your photos'}
        </p>
        <p className="mt-2 text-gray-500">
          or <span className="text-blue-600 font-medium">browse</span> to choose files
        </p>
        
        <div className="mt-6 flex items-center justify-center gap-6 text-sm text-gray-400">
          <span className="flex items-center gap-1.5">
            <Image className="w-4 h-4" />
            JPG
          </span>
          <span className="flex items-center gap-1.5">
            <FileImage className="w-4 h-4" />
            PNG
          </span>
          <span className="flex items-center gap-1.5">
            <Image className="w-4 h-4" />
            WebP
          </span>
        </div>
        
        <p className="mt-2 text-xs text-gray-400">
          Maximum file size: 50MB per photo
        </p>
      </label>
    </div>
  )
}